import { Bot, ExternalLink, Terminal, Repeat } from 'lucide-react'
import ModuleLayout from '../components/ModuleLayout'
import StepByStep from '../components/StepByStep'
import CopyBlock from '../components/CopyBlock'
import SpreadsheetGrid from '../components/SpreadsheetGrid'
import SheetSubmission from '../components/SheetSubmission'
import {
  AP_HEADERS, AP_TAB_NAME, ROW_COUNT, STATUSES, APPROVERS, VENDORS,
  AMOUNT_RANGE, DATE_RANGE, PLANTED_DUPLICATES, SAMPLE_ROWS, money,
} from '../data/apDataset'
import { MODULE_25_CHECKS } from '../data/apChecks'

/**
 * Module 2 of the AI track (id 25).
 *
 * The predecessor of this module handed the trainee a block of invoice data to
 * paste into their prompt. That is a demonstration of the clipboard: if you
 * already hold the rows, putting them in the sheet yourself is quicker, and
 * the lesson taken away is that the terminal is a slow way to paste.
 *
 * Here they describe the register they want and Claude writes an Apps Script
 * that generates it. Nobody's data matches anybody else's, which is why
 * apChecks asserts shape and self-consistency rather than values.
 */

const PROMPT = `Here is my Google Sheet:
<paste your sheet link here>

I want a practice accounts-payable register. Please write a Google Apps Script
that builds it, then tell me how to run it.

The script should create a tab called "${AP_TAB_NAME}" with these headings in
row 1, in this order:

${AP_HEADERS.join(', ')}

Then fill ${ROW_COUNT} rows of made-up but believable data below them:

  Invoice   a unique reference like INV-1042, no two the same
  Vendor    picked from: ${VENDORS.join(', ')}
  Date      spread across ${DATE_RANGE.from} to ${DATE_RANGE.to}
  Amount    a number between ${AMOUNT_RANGE.min} and ${AMOUNT_RANGE.max}, most of them
            small, a few very large — real registers are lopsided
  Status    one of ${STATUSES.join(', ')}, with all three appearing
  Approver  either "${APPROVERS[0]}" or "${APPROVERS[1]}"

One more thing: plant exactly ${PLANTED_DUPLICATES} duplicate — two rows with the same
vendor and the same amount, a few days apart, with different invoice numbers.
I want something to find in a later exercise.

Write the amounts as numbers, not text. Don't put formulas in the cells.`

function SectionWhatYoullDo() {
  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Describe it, don't type it</h2>
      <p className="mb-4 text-slate-600">
        You need a register of invoices to practise on — forty of them. You could type them. You
        could paste them from somewhere. Instead you're going to describe what the table should
        look like, and have Claude write a small program that produces it.
      </p>
      <p className="mb-4 text-slate-600">
        That difference is the entire module. Pasting gives you one table. A script gives you a
        table you can regenerate, change your mind about, hand to a colleague, or run again next
        quarter with different dates.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border-2 border-slate-200 bg-white p-4">
          <div className="mb-2 flex items-center gap-2 font-semibold text-slate-700">
            <Bot className="h-4 w-4" /> Asking for the data
          </div>
          <p className="text-sm text-slate-600">
            You get rows. If you want forty more, or the same thing for another quarter, you ask
            again and hope it comes out consistent.
          </p>
        </div>
        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4">
          <div className="mb-2 flex items-center gap-2 font-semibold text-emerald-900">
            <Repeat className="h-4 w-4" /> Asking for the thing that makes the data
          </div>
          <p className="text-sm text-emerald-800">
            You get rows <em>and</em> the recipe. Run it again, change one line, give it to someone
            else. This is the habit worth building.
          </p>
        </div>
      </div>
    </section>
  )
}

function SectionShape() {
  const grid = [AP_HEADERS, ...SAMPLE_ROWS.map((r) => [r[0], r[1], r[2], `$${money(r[3])}`, r[4], r[5]])]
  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">The shape you're describing</h2>
      <p className="mb-4 text-slate-600">
        Roughly like this, forty rows deep. <strong>Yours will not match this and that's fine</strong> —
        the amounts, vendors and dates are generated, so everyone's register is different. What has
        to match is the shape: these six headings, in this order.
      </p>
      <SpreadsheetGrid data={grid} compact />
      <p className="mt-2 text-sm text-slate-500">
        Illustration only — four rows of somebody else's register.
      </p>
    </section>
  )
}

function SectionDoIt() {
  const steps = [
    {
      title: 'Ask for the script',
      content: (
        <div className="space-y-3">
          <p>
            Same sheet as last time. Open <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">wa</code>,
            paste this in with your link at the top, and read what comes back before you run it.
          </p>
          <CopyBlock text={PROMPT} label="Copy prompt" />
          <p className="text-sm text-slate-500">
            Notice how much of that is the <em>specification</em> — column order, value ranges, what
            counts as believable. That's the part you can't skip, and it's the part a colleague
            would have asked you about anyway.
          </p>
        </div>
      ),
      highlight:
        'It may offer to run the script for you, or ask you to paste it into the editor. Either is fine — the next module is about that editor.',
    },
    {
      title: 'Run it',
      content: (
        <div className="space-y-2">
          <p>
            If Claude hands you code, open your sheet and go to{' '}
            <strong>Extensions ▸ Apps Script</strong>, paste it in, press <strong>Run</strong>, and
            approve the permission dialog Google shows you.
          </p>
          <p className="text-sm text-slate-500">
            The permission dialog is Google asking whether this script may touch your spreadsheet —
            it looks alarming and it's routine. If you want to know exactly what you're agreeing to,
            do{' '}
            <a href="/courses/apps-script" className="text-blue-600 hover:underline">
              Your First Apps Script
            </a>{' '}
            in the Apps Script course; it's built for this moment.
          </p>
        </div>
      ),
    },
    {
      title: 'Look at what it made',
      content: (
        <div className="space-y-2">
          <p>
            Forty rows you didn't type. Scroll them. Are the amounts plausible? Do the dates sit in
            the right quarter? Is there a duplicate in there somewhere?
          </p>
          <p>
            If something looks wrong, say so and ask for a fix — then run it again. The script is
            the thing you're iterating on, not the rows.
          </p>
        </div>
      ),
      highlight: "Check it. A generated table is exactly as trustworthy as the description you gave.",
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Do it</h2>
      <StepByStep steps={steps} />
    </section>
  )
}

function SectionWhatJustHappened() {
  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">What you actually got</h2>
      <div className="space-y-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">A program, not an answer</h3>
          <p className="mt-1 text-sm text-slate-600">
            The rows are the output. The script is the asset — and it's sitting in your spreadsheet,
            saved, ready to run again. That's the difference between asking a model to do a thing
            and asking it to build the thing that does it.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">The description did the work</h3>
          <p className="mt-1 text-sm text-slate-600">
            Column order, value ranges, "most small, a few very large", a planted duplicate. Leave
            any of it out and you'd have got something — just not this. Next module is about
            exactly that.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">Nobody else's register looks like yours</h3>
          <p className="mt-1 text-sm text-slate-600">
            So the check below can't compare your numbers to a right answer — there isn't one. It
            checks the shape you were asked for, and later on it checks that your summaries agree
            with your own rows. Which is what anyone reviewing your work would actually do.
          </p>
        </div>
      </div>
    </section>
  )
}

function SectionTroubleshooting() {
  const problems = [
    {
      p: 'It asks for permission before the script will run',
      s: 'Expected, and it comes in two steps on a Finder account: Review permissions, then Continue. Read what it asks for — the scope covers every spreadsheet you can open, not just this one. If you instead see a warning that the app is unverified, you are signed in with a personal Google account rather than your work one.',
    },
    {
      p: 'The script ran but the sheet is empty',
      s: 'It probably wrote to a different tab or a different spreadsheet. Ask: "which spreadsheet and tab did that write to?" — then tell it where you meant.',
    },
    {
      p: 'The amounts came out as text, or every row is identical',
      s: 'Say exactly that. "The Amount column is text, make them numbers" or "every row is the same, vary them" — then run it again.',
    },
    {
      p: 'It gave you a script but no instructions',
      s: 'Ask "how do I run this?". Being told to paste code with no idea where is the most common way people stall here.',
    },
    {
      p: 'There is no duplicate in the data',
      s: 'The check below will tell you. Ask it to plant one — same vendor and amount, different invoice number.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">If it goes wrong</h2>
      <p className="mb-4 text-slate-600">
        All of this is normal, and none of it means you've broken anything. Say what's wrong in
        plain English and run it again — that's the loop.
      </p>
      <dl className="space-y-3">
        {problems.map(({ p, s }) => (
          <div key={p} className="rounded-lg border border-slate-200 bg-white p-4">
            <dt className="flex items-start gap-2 text-sm font-semibold text-slate-800">
              <Terminal className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              {p}
            </dt>
            <dd className="mt-1.5 pl-6 text-sm text-slate-600">{s}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-sm text-slate-500">
        Lost the sheet entirely?{' '}
        <a
          href="https://sheets.new"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
        >
          Start a fresh one <ExternalLink className="h-3 w-3" />
        </a>{' '}
        and run the prompt again — it takes a minute, which is rather the point.
      </p>
    </section>
  )
}

export default function ModuleAskForData() {
  return (
    <ModuleLayout moduleId={25}>
      <div className="space-y-12">
        <SectionWhatYoullDo />
        <SectionShape />
        <SectionDoIt />
        <SectionWhatJustHappened />
        <SectionTroubleshooting />
        <SheetSubmission
          moduleId={25}
          checks={MODULE_25_CHECKS}
          sourceModuleId={19}
          heading="Check your register"
          intro="Same spreadsheet as last time — the link is filled in for you. We can't check your numbers against anyone else's, so this checks the shape you asked for."
        />
      </div>
    </ModuleLayout>
  )
}
